import {
  useGetDocumentApproversQuery,
  useGetUser,
  useGetUserById,
} from "@hooks";
import type { Document, DocumentVersion } from "@models";
import { Box, Collapse, Tab, Tabs } from "@mui/material";
import {
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useParams } from "react-router-dom";
import ApproversTabContentLoading from "src/components/DocViewer/components/docInfoDrawer/approversTab/ApproversTabContentLoading";
import CompleteScreen from "src/components/DocViewer/components/docInfoDrawer/approversTab/CompleteScreen";
import ApproversTabForm from "src/components/DocViewer/components/docInfoDrawer/approversTab/formContent/ApproversTabForm";
import { getDocVersionRevisionNumber } from "src/utils";
import { TiptapSignatureTable } from "./TiptapSignatureTable";

type TiptapApproversTabView = "approval" | "signatureTable";

const APPROVERS_TAB_TOP_RADIUS = "7px";
const tabheigtandpaddings = "90px";

export const TiptapApproversTab = ({
  actions,
  isEditMode,
  onApprovalSubmitSettled,
  onApprovalSubmitStart,
  onBeforeSubmitApproval,
  selectedDoc,
  selectedDocVersion,
  setIsEditMode,
}: {
  actions?: ReactNode;
  deviceId: string;
  isEditMode: boolean;
  onApprovalSubmitSettled?: () => void;
  onApprovalSubmitStart?: () => void;
  onBeforeSubmitApproval?: () => Promise<void>;
  selectedDoc: Document;
  selectedDocVersion: DocumentVersion;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
}) => {
  const docVersionAuthor = selectedDocVersion.createdByUser;

  return (
    <TiptapApproversTabContent
      key={selectedDocVersion.id}
      isEditMode={isEditMode}
      onApprovalSubmitSettled={onApprovalSubmitSettled}
      onApprovalSubmitStart={onApprovalSubmitStart}
      onBeforeSubmitApproval={onBeforeSubmitApproval}
      selectedDoc={selectedDoc}
      selectedDocVersion={selectedDocVersion}
      setIsEditMode={setIsEditMode}
      docVersionAuthor={docVersionAuthor}
      actions={actions}
    />
  );
};

const TiptapApproversTabContent = ({
  actions,
  docVersionAuthor,
  isEditMode,
  onApprovalSubmitSettled,
  onApprovalSubmitStart,
  onBeforeSubmitApproval,
  selectedDoc,
  selectedDocVersion,
  setIsEditMode,
}: {
  actions?: ReactNode;
  docVersionAuthor: DocumentVersion["createdByUser"];
  isEditMode: boolean;
  onApprovalSubmitSettled?: () => void;
  onApprovalSubmitStart?: () => void;
  onBeforeSubmitApproval?: () => Promise<void>;
  selectedDoc: Document;
  selectedDocVersion: DocumentVersion;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
}) => {
  const [activeTab, setActiveTab] =
    useState<TiptapApproversTabView>("approval");
  const { data: user } = useGetUser();
  const { data: documentCreator } = useGetUserById({
    userId: selectedDoc.createdBy,
  });
  const { orgId = "" } = useParams();

  const { data: approvers } = useGetDocumentApproversQuery({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
  });

  if (!docVersionAuthor || !user || approvers === undefined) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <TiptapApproversTabHeader
          actions={actions}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="min-h-0 flex-1 overflow-auto pt-4">
          <ApproversTabContentLoading />
        </div>
      </div>
    );
  }

  const creator = documentCreator ?? docVersionAuthor;
  const approvalContent =
    selectedDocVersion.revision !== null ? (
      <CompleteScreen
        approvers={approvers}
        author={docVersionAuthor}
        creator={creator}
        documentCreatedAt={selectedDoc.createdAt}
        docVersion={selectedDocVersion}
        revision={getDocVersionRevisionNumber(
          selectedDocVersion,
          selectedDoc.versions
        )}
        revisionSummary={selectedDocVersion.revisionSummary}
      />
    ) : (
      <ApproversTabForm
        selectedDocVersion={selectedDocVersion}
        selectedDoc={selectedDoc}
        docId={selectedDoc.id}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onApprovalSubmitSettled={onApprovalSubmitSettled}
        onApprovalSubmitStart={onApprovalSubmitStart}
        onBeforeSubmitApproval={onBeforeSubmitApproval}
      />
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <TiptapApproversTabHeader
        actions={actions}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="min-h-0 overflow-hidden">
        <TiptapApproversTabPanel activeTab={activeTab} tab="approval">
          {approvalContent}
        </TiptapApproversTabPanel>
        <TiptapApproversTabPanel activeTab={activeTab} tab="signatureTable">
          {selectedDocVersion.signatureTable ? (
            <TiptapSignatureTable
              signatureTable={selectedDocVersion.signatureTable}
            />
          ) : (
            <ApproversTabContentLoading />
          )}
        </TiptapApproversTabPanel>
      </div>
    </div>
  );
};

const TiptapApproversTabHeader = ({
  actions,
  activeTab,
  onTabChange,
}: {
  actions?: ReactNode;
  activeTab: TiptapApproversTabView;
  onTabChange: (tab: TiptapApproversTabView) => void;
}) => (
  <Box
    sx={{
      alignItems: "center",
      display: "flex",
      gap: 2,
      justifyContent: "space-between",
    }}
  >
    <Tabs
      value={activeTab}
      onChange={(_, value: TiptapApproversTabView) => onTabChange(value)}
      aria-label="Tiptap approval tabs"
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        "& #tiptap-approvers-approval-tab": {
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.12)",
        },
        "& #tiptap-approvers-signatureTable-tab": {
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.12)",
        },
        "& .MuiTab-root": {
          borderTopLeftRadius: APPROVERS_TAB_TOP_RADIUS,
          borderTopRightRadius: APPROVERS_TAB_TOP_RADIUS,
          overflow: "hidden",
        },
        "& .MuiTab-root.Mui-selected": {
          backgroundColor: "#f8fafc",
        },
        "& .MuiTabs-indicator": {
          borderTopLeftRadius: APPROVERS_TAB_TOP_RADIUS,
          borderTopRightRadius: APPROVERS_TAB_TOP_RADIUS,
        },
      }}
    >
      <Tab
        value="approval"
        label="Approval"
        {...getTiptapApproversTabProps("approval")}
      />
      <Tab
        value="signatureTable"
        label="Signature table"
        {...getTiptapApproversTabProps("signatureTable")}
      />
    </Tabs>
    {actions ? (
      <div className="flex shrink-0 items-center">{actions}</div>
    ) : null}
  </Box>
);

const getTiptapApproversTabProps = (tab: TiptapApproversTabView) => ({
  id: `tiptap-approvers-${tab}-tab`,
  "aria-controls": `tiptap-approvers-${tab}-tabpanel`,
});

const TiptapApproversTabPanel = ({
  activeTab,
  children,
  tab,
}: {
  activeTab: TiptapApproversTabView;
  children: ReactNode;
  tab: TiptapApproversTabView;
}) => (
  <Collapse in={activeTab === tab} unmountOnExit>
    <div
      role="tabpanel"
      id={`tiptap-approvers-${tab}-tabpanel`}
      aria-labelledby={`tiptap-approvers-${tab}-tab`}
      className="min-h-0 overflow-auto pt-4"
      style={{ maxHeight: `calc(82vh - ${tabheigtandpaddings})` }}
    >
      {children}
    </div>
  </Collapse>
);

export default TiptapApproversTab;
